import { useState } from 'react'
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor, pointerWithin, rectIntersection,
  useSensor, useSensors,
} from '@dnd-kit/core'
import type { CollisionDetection, DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { deleteOriginalTrophy, getOriginalTrophies } from '@/api/trophies'
import type { OriginalTrophy } from '@/api/trophies'
import { useShelfLayout } from '@/hooks/useShelfLayout'
import { parseDragId, parseDropId } from '@/lib/shelf-layout'
import FreeShelf from './trophy/FreeShelf'
import OwnedTrophyList from './trophy/OwnedTrophyList'
import TrophyGenerateDialog from './trophy/TrophyGenerateDialog'
import TrophyRevealOverlay from './trophy/TrophyRevealOverlay'
import TrophyDetailDialog from './trophy/TrophyDetailDialog'
import { TrophyFigure } from './trophy/trophyShared'

// 広い一覧に吸われないよう指の位置で判定し、どこにも入っていない時だけ重なり面積で判定する
const collisionDetection: CollisionDetection = (args) => {
  const hits = pointerWithin(args)
  return hits.length > 0 ? hits : rectIntersection(args)
}

export default function TrophyTab() {
  const [genOpen, setGenOpen] = useState(false)
  const [revealTrophy, setRevealTrophy] = useState<OriginalTrophy | null>(null)
  const [detailTrophy, setDetailTrophy] = useState<OriginalTrophy | null>(null)
  const [dragTrophy, setDragTrophy] = useState<OriginalTrophy | null>(null)

  const qc = useQueryClient()
  const { data: trophies = [], isLoading } = useQuery({
    queryKey: ['originalTrophies'],
    queryFn: getOriginalTrophies,
  })
  const shelf = useShelfLayout(trophies)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  )

  const handleDragStart = (e: DragStartEvent) => {
    const source = parseDragId(String(e.active.id))
    setDragTrophy(trophies.find((t) => t.id === source?.trophyId) ?? null)
  }

  const handleDragEnd = (e: DragEndEvent) => {
    setDragTrophy(null)
    const source = parseDragId(String(e.active.id))
    const target = e.over ? parseDropId(String(e.over.id)) : null
    if (source && target) shelf.drop(source, target)
  }

  // 詳細の「棚に追加」：未配置トロフィーを最初の空きスロットに置く（保存はドラッグと同じ経路）
  const addToShelf = (t: OriginalTrophy) => {
    setDetailTrophy(null)
    void shelf.placeInFirstEmptySlot(t.id)
  }

  // 成功時 true / 失敗時 false を返す（呼び出し側が詳細を閉じるか判断する）
  const deleteTrophy = async (t: OriginalTrophy): Promise<boolean> => {
    try {
      await deleteOriginalTrophy(t.id)
      qc.invalidateQueries({ queryKey: ['originalTrophies'] })
      qc.invalidateQueries({ queryKey: ['trophyQuota'] })
      setDetailTrophy(null)
      toast.success('トロフィーを削除しました')
      return true
    } catch {
      toast.error('削除に失敗しました')
      return false
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">オリジナルトロフィー</p>
        <Button size="sm" className="gap-1.5" onClick={() => setGenOpen(true)}>
          <Sparkles size={14} />
          生成
        </Button>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">読み込み中...</div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setDragTrophy(null)}
        >
          <FreeShelf
            trophies={trophies}
            layout={shelf.layout}
            onApply={shelf.apply}
            onDelete={deleteTrophy}
          />
          <OwnedTrophyList trophies={trophies} onTap={(t) => setDetailTrophy(t)} />
          <DragOverlay>
            {dragTrophy ? <TrophyFigure trophy={dragTrophy} /> : null}
          </DragOverlay>
        </DndContext>
      )}

      <TrophyGenerateDialog
        open={genOpen}
        onClose={() => setGenOpen(false)}
        onGenerated={(t) => setRevealTrophy(t)}
      />
      <TrophyRevealOverlay
        trophy={revealTrophy}
        onDone={(t) => { setRevealTrophy(null); setDetailTrophy(t) }}
      />
      <TrophyDetailDialog
        trophy={detailTrophy}
        onClose={() => setDetailTrophy(null)}
        onAddToShelf={addToShelf}
        onDelete={deleteTrophy}
      />
    </div>
  )
}
